import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'motion/react';
import { Search, MapPin, Users, BookOpen, Tag, ChevronRight, Calendar, ChevronLeft, ArrowUp, Bell, GraduationCap } from 'lucide-react';
import logoImage from '../assets/f736bb54a1ccb5a9a30d8b8ec9d3974d4b7ef9d6.png';
import logoImageWhite from '../assets/fac7d53f9fcb6f491ee2fb6641c18821c53c606e.png';
import goldIcon from '../assets/gold icon.png';
import { Input } from './ui/input';

import { IdentityExpansionModal } from './IdentityExpansionModal';
import { Button } from './ui/button';
import { useUserData } from './UserDataContext';
import { useScreenReady } from '../contexts/ScreenTransitionContext';
import { useContentFilter } from '../hooks/useContentFilter';
import { toast } from 'sonner';
import { FlagImage, allCountries } from './utils/countryFlags';
import { CountryName } from './ui/CountryName';
import { ParallaxBackground } from './utils/parallax';
import { useTheme } from './ThemeProvider';
import { AnimatePresence } from 'motion/react';
import foodOfDayImage from '../assets/foodofweek.jpg';
import { auth } from '../services/firebaseConfig';
import { collection, query, where, orderBy, onSnapshot, getDoc, doc } from 'firebase/firestore';
import { SCREENS, EVENTS } from '../constants/navigation';

import { CountrySelector } from './CountrySelector';
import { PressableCard } from './ui/PressableCard';
import { CommunityFilterBar } from './CommunityFilterBar';
import { CommunityDiscoveryService, CommunityFilter } from '../services/CommunityDiscoveryService';
import { useNotificationBadges } from './NotificationContext';
import { SearchService } from '../services/api';
import { db } from '../services/firebaseConfig';
import { SafeStorage } from '../utils/safeStorage';
import { LegionEventManagerModal } from './LegionEventManagerModal';
import { ShieldAlert } from 'lucide-react';
import { InviteFloatingButton } from './InviteFloatingButton';
import { PILAU_SECTIONS } from './EventDetailsScreen';
import { BusinessExploreDeck } from './business/BusinessExploreDeck';
import { BusinessService } from '../services/BusinessService';
import type { BusinessProfile } from '../types/business';

// Remove static tribes
// const tribes = [...]



import { identityData, identityCategories, categoryStyles } from '../constants/identities';
import { IdentityTag } from './IdentityTag';

const historyContent = [
  {
    id: 'ancient-egypt',
    title: 'Ancient Egypt: Masters of Innovation',
    description: 'Discover how ancient Egyptians pioneered mathematics, architecture, and medicine',
    image: 'https://images.unsplash.com/photo-1630776212743-6d31601fd616?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxhbmNpZW50JTIwZWd5cHQlMjBweXJhbWlkcyUyMHNwaGlueHxlbnwxfHx8fDE3NjEzMzUwMDl8MA&ixlib=rb-4.1.0&q=80&w=1080',
    posts: 2847,
  },
  {
    id: 'kingdom-of-kush',
    title: 'Kingdom of Kush: The Black Pharaohs',
    description: 'The powerful Nubian rulers who conquered and ruled ancient Egypt',
    image: 'https://images.unsplash.com/photo-1667765460178-db9eae077f00?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxudWJpYW4lMjBweXJhbWlkcyUyMHN1ZGFufGVufDF8fHx8MTc2MTQzMjMzNXww&ixlib=rb-4.1.0&q=80&w=1080',
    posts: 1523,
  },
  {
    id: 'great-zimbabwe',
    title: 'Great Zimbabwe: Stone City Empire',
    description: 'Exploring the medieval trading empire and its magnificent architecture',
    image: 'https://images.unsplash.com/photo-1655981654045-5c45b14f3bc2?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxncmVhdCUyMHppbWJhYmdlJTIwc3RvbmUlMjBydWluc3xlbnwxfHx8fDE3NjE0NDg5MzV8MA&ixlib=rb-4.1.0&q=80&w=1080',
    posts: 1186,
  },
  {
    id: 'mali-empire',
    title: 'Mali Empire: Golden Age of Timbuktu',
    description: 'Mansa Musa and the intellectual center of the medieval world',
    image: 'https://images.unsplash.com/photo-1592923181985-7a410f8912f5?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxtYWxpJTIwZW1waXJlJTIwdGltYnVrdHUlMjBtb3NxdWV8ZW58MXx8fHwxNzYxNDQ4OTM1fDA&ixlib=rb-4.1.0&q=80&w=1080',
    posts: 2134,
  },
  {
    id: 'benin-kingdom',
    title: 'Benin Kingdom: Bronze Masterpieces',
    description: 'The artistic brilliance and royal heritage of the Edo people',
    image: 'https://images.unsplash.com/photo-1608657282762-d925604f8f03?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxiZW5pbiUyMGJyb256ZSUyMGFydGlmYWN0c3xlbnwxfHx8fDE3NjE0NDg5MzV8MA&ixlib=rb-4.1.0&q=80&w=1080',
    posts: 987,
  },
  {
    id: 'ethiopian-empire',
    title: 'Ethiopian Empire: Ancient Christianity',
    description: 'The Aksumite Kingdom and rock-hewn churches of Lalibela',
    image: 'https://images.unsplash.com/photo-1649953332363-aba2f565b52a?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxsYWxpYmVsYSUyMGV0aGlvcGlhJTIwcm9jayUyMGNodXJjaHxlbnwxfHx8fDE3NjE0NDg5MzZ8MA&ixlib=rb-4.1.0&q=80&w=1080',
    posts: 1654,
  },
  {
    id: 'songhai-empire',
    title: 'Songhai Empire: West African Power',
    description: 'The largest empire in African history spanning the Niger River',
    image: 'https://images.unsplash.com/photo-1565846894054-51426d448b96?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxuaWdlciUyMHJpdmVyJTIwd2VzdCUyMGFmcmljYXxlbnwxfHx8fDE3NjE0NDg5MzZ8MA&ixlib=rb-4.1.0&q=80&w=1080',
    posts: 1342,
  },
  {
    id: 'ashanti-kingdom',
    title: 'Ashanti Kingdom: Golden Legacy',
    description: 'The powerful Gold Coast empire and the sacred Golden Stool',
    image: 'https://images.unsplash.com/photo-1736612270274-ab0e89bb14a8?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxhc2hhbnRpJTIwZ2hhbmElMjBnb2xkJTIwc3Rvb2x8ZW58MXx8fHwxNzYxNDQ4OTM2fDA&ixlib=rb-4.1.0&q=80&w=1080',
    posts: 1789,
  },
];

const getGradient = (id: string = '') => {
  const gradients = [
    'linear-gradient(135deg, #FF9966 0%, #FF5E62 100%)',
    'linear-gradient(135deg, #56CCF2 0%, #2F80ED 100%)',
    'linear-gradient(135deg, #4776E6 0%, #8E54E9 100%)',
    'linear-gradient(135deg, #00b09b 0%, #96c93d 100%)',
    'linear-gradient(135deg, #D4145A 0%, #FBB03B 100%)',
    'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  ];
  let hash = 0;
  for (let i = 0; i < id.length; i++) {
    hash = id.charCodeAt(i) + ((hash << 5) - hash);
  }
  return gradients[Math.abs(hash) % gradients.length];
};

export function ExploreScreen() {
  const { userProfile, updateProfile } = useUserData();
  const { isMutedGroup } = useContentFilter();
  const { unreadCount } = useNotificationBadges();
  const { theme } = useTheme();
  const [showIdentityModal, setShowIdentityModal] = useState(false);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [showScrollToTop, setShowScrollToTop] = useState(false);
  const [communityNotifications, setCommunityNotifications] = useState<any[]>([]);
  const [showLegionEventManager, setShowLegionEventManager] = useState(false);
  const [liveEvents, setLiveEvents] = useState<any[]>([]);
  const [activeEventIndex, setActiveEventIndex] = useState(0);
  const [direction, setDirection] = useState(0);

  const [tribeFilter, setTribeFilter] = useState<CommunityFilter>('trending');
  const [tribes, setTribes] = useState<any[]>([]);
  const [forumFilter, setForumFilter] = useState<CommunityFilter>('trending');
  const [forums, setForums] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  
  // Countries State
  const [countries, setCountries] = useState<any[]>([]);

  useEffect(() => {
    // Country counts are pre-computed by the nightly rollup CF into platform_stats/global.
    // Reading from there avoids direct users-collection list queries (which are now
    // bracket-gated by the Phase 2d security rule and cannot produce total cross-bracket counts).
    const fetchCountryCounts = async () => {
      try {
        // Trigger a refresh first (10-min server-side cooldown means this is
        // cheap on repeat calls). Self-heals if platform_stats was never written.
        try {
          const { getFunctions, httpsCallable } = await import('firebase/functions');
          const { getApp } = await import('firebase/app');
          const fn = httpsCallable(getFunctions(getApp()), 'refreshCountryCounts');
          const result = await fn();
          console.log('[Explore] refreshCountryCounts result:', result.data);
        } catch (e) {
          console.warn('[Explore] refreshCountryCounts FAILED:', (e as any)?.message, e);
        }

        const globalSnap = await getDoc(doc(db, 'platform_stats', 'global'));
        const countryCounts: Record<string, number> = globalSnap.data()?.countryCounts ?? {};

        const topCountries = allCountries.map((c) => {
          // Older users store only `country` (full name); newer users store
          // `countryFlag` (ISO). The rollup writes both key formats, so try
          // ISO first, then fall back to the full name.
          const count = countryCounts[c.code] ?? countryCounts[c.name] ?? 0;
          const formattedCount = count >= 1000 ? (count / 1000).toFixed(1) + 'k' : count.toString();
          return { ...c, members: formattedCount, rawCount: count };
        });

        const sorted = topCountries
          .sort((a, b) => b.rawCount - a.rawCount)
          .slice(0, 6);

        setCountries(sorted);
      } catch (error) {
        console.error('Error fetching country counts:', error);
      }
    };

    fetchCountryCounts();
  }, []);

  // Fetch live events from Firestore
  useEffect(() => {
    let unsubscribe: (() => void) | null = null;
    
    const setupEventsListener = async () => {
      try {
        const db = (await import('../services/firebaseConfig')).getDbSafe();
        const resolvedDb = await db;
        
        const eventsQuery = query(collection(resolvedDb, 'events'), where('status', '==', 'active'));
        
        unsubscribe = onSnapshot(eventsQuery, (snapshot) => {
          const now = new Date();
          const active = snapshot.docs
            .map(d => {
              const data = d.data() as any;
              let foodSections: any[] = [];
              if (data.foodSections) {
                try {
                  foodSections = typeof data.foodSections === 'string'
                    ? JSON.parse(data.foodSections)
                    : data.foodSections;
                } catch (e) {
                  console.warn('[ExploreScreen] Failed to parse foodSections', e);
                }
              }
              return { id: d.id, ...data, foodSections };
            })
            .filter((e: any) => {
              if (!e.expiresAt) return false;
              const exp = e.expiresAt?.toDate ? e.expiresAt.toDate() : new Date(e.expiresAt);
              return exp > now;
            })
            .sort((a: any, b: any) => {
              // Sort by manual order first
              if (a.order !== undefined && b.order !== undefined) return a.order - b.order;
              if (a.order !== undefined) return -1;
              if (b.order !== undefined) return 1;
              
              // Fallback to start/expiry date (descending, match manager)
              const getVal = (v: any) => v?.toDate ? v.toDate().getTime() : (v ? new Date(v).getTime() : 0);
              const timeA = Math.max(getVal(a.startsAt), getVal(a.expiresAt));
              const timeB = Math.max(getVal(b.startsAt), getVal(b.expiresAt));
              return timeB - timeA;
            });
            
          setLiveEvents(active);
        }, (error) => {
          console.error("Explore events listener error:", error);
        });
        
      } catch (err) {
        console.error("Failed to setup events listener:", err);
      }
    };
    
    setupEventsListener();
    
    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, []);
    
  // Auto-advance events carousel every 3 seconds
  useEffect(() => {
    const list = (liveEvents && liveEvents.length > 1) ? liveEvents : [];
    if (list.length <= 1) return;
    
    const interval = setInterval(() => {
        setDirection(1);
        setActiveEventIndex(current => (current + 1) % list.length);
    }, 3000);
    
    return () => clearInterval(interval);
  }, [liveEvents, direction]); // Reset on manual click (direction changes)
  
  useEffect(() => {
    setActiveEventIndex(0);
  }, [liveEvents.length, (liveEvents[0] as any)?.id]);


  useScreenReady(!isLoading);

  // Search State
  const [searchQuery, setSearchQuery] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  
  // Ensure search is cleared on mount to prevent stale/autofill ghost overlays
  useEffect(() => {
    setSearchQuery('');
    setShowSearchDropdown(false);
    setIsSearching(false);
  }, []);
  const [searchExploreResults, setSearchExploreResults] = useState<{
    countries: any[],
    forums: any[],
    tribes: any[],
    regions: any[]
  }>({ countries: [], forums: [], tribes: [], regions: [] });
  // Business search runs alongside searchExplore rather than inside it: the
  // shared helper returns a fixed {countries, forums, tribes, regions} shape
  // consumed elsewhere, and businesses are their own collection with their own
  // rules. Kept separate so neither has to know about the other.
  const [businessResults, setBusinessResults] = useState<BusinessProfile[]>([]);
  const [searchRect, setSearchRect] = useState<DOMRect | null>(null);

  const searchContainerRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const searchTimeoutRef = useRef<NodeJS.Timeout | undefined>(undefined);

  // Debounced Search Logic
  useEffect(() => {
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);

    const q = searchQuery.trim();
    if (q.length > 0) {
      console.log('[ExploreScreen] Starting search for:', q);
      setIsSearching(true);
      // Only show dropdown if the user is actually focused on the search bar
      if (typeof document !== 'undefined' && document.activeElement === searchInputRef.current) {
        setShowSearchDropdown(true);
      }
      searchTimeoutRef.current = setTimeout(async () => {
        try {
          console.time(`Search-${q}`);
          const [results, businesses] = await Promise.all([
            SearchService.searchExplore(q, isMutedGroup),
            // Prefix-matched on businessNameLower, so a partial name finds the
            // business — there are no usernames here to fall back on.
            BusinessService.searchBusinesses(q),
          ]);
          console.timeEnd(`Search-${q}`);
          console.log(`[ExploreScreen] SUCCESS: Results for "${q}":`, {
            countries: results.countries.length,
            forums: results.forums.length,
            tribes: results.tribes.length,
            data: results
          });

          if (q === searchQuery.trim()) {
            setSearchExploreResults(results);
            setBusinessResults(businesses);
          } else {
            console.warn(`[ExploreScreen] DROPPING results for "${q}" because current query is "${searchQuery.trim()}"`);
          }
        } catch (e) {
          console.error(`[ExploreScreen] ERROR: Search failed for "${q}":`, e);
        } finally {
          setIsSearching(false);
        }
      }, 400);
    } else {
      setSearchExploreResults({ countries: [], forums: [], tribes: [], regions: [] });
      setBusinessResults([]);
      setIsSearching(false);
      setShowSearchDropdown(false);
    }

    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, [searchQuery]);

  // Update rect whenever dropdown is shown
  useEffect(() => {
    if (showSearchDropdown && searchContainerRef.current) {
      setSearchRect(searchContainerRef.current.getBoundingClientRect());
    }
  }, [showSearchDropdown]);

  // Update portal position on scroll
  useEffect(() => {
    const updatePosition = () => {
      if (showSearchDropdown && searchContainerRef.current) {
        setSearchRect(searchContainerRef.current.getBoundingClientRect());
      }
    };

    if (showSearchDropdown) {
      window.addEventListener('scroll', updatePosition, true);
      return () => window.removeEventListener('scroll', updatePosition, true);
    }
  }, [showSearchDropdown]);

  // Close dropdown on click outside - accounting for Portal
  useEffect(() => {
    const handleClickOutside = (event: PointerEvent) => {
      const dropdownElement = document.getElementById('search-dropdown-portal');
      const isClickInsideSearch = searchContainerRef.current?.contains(event.target as Node);
      const isClickInsideDropdown = dropdownElement?.contains(event.target as Node);

      if (!isClickInsideSearch && !isClickInsideDropdown) {
        setShowSearchDropdown(false);
      }
    };
    document.addEventListener('pointerdown', handleClickOutside);
    return () => document.removeEventListener('pointerdown', handleClickOutside);
  }, []);

  // Fetch Data - Reverted to Service for stability/fallbacks
  useEffect(() => {
    let isActive = true;

    const fetchData = async () => {
      setIsLoading(true);
      try {
        // Use the service which has robust fallbacks for missing indexes/permissions
        const [tribesData, forumsData] = await Promise.all([
          CommunityDiscoveryService.getCommunities(
            'tribes',
            tribeFilter,
            userProfile.userTags || [],
            6,
            isMutedGroup,
          ),
          CommunityDiscoveryService.getCommunities(
            'forums',
            forumFilter,
            userProfile.userTags || [],
            6,
            isMutedGroup,
          )
        ]);

        if (isActive) {
          setTribes(tribesData);
          setForums(forumsData);

          // Fetch accurate post counts from the actual collections
          try {
            const db = (await import('../services/firebaseConfig')).getDbSafe();
            const resolvedDb = await db;
            
            // 1. Fetch Forum Post Counts
            const forumsWithCounts = await Promise.all(
              forumsData.map(async (forum: any) => {
                try {
                  const postsQuery = query(
                    collection(resolvedDb, 'forum_posts'),
                    where('topicId', '==', forum.id)
                  );
                  const countSnapshot = await getCountFromServer(postsQuery);
                  return { ...forum, postCount: countSnapshot.data().count };
                } catch (e) {
                  return forum;
                }
              })
            );

            // 2. Fetch Tribe Post Counts
            const tribesWithCounts = await Promise.all(
              tribesData.map(async (tribe: any) => {
                try {
                  const postsQuery = query(
                    collection(resolvedDb, 'tribe_posts'),
                    where('tribeId', '==', tribe.id)
                  );
                  const countSnapshot = await getCountFromServer(postsQuery);
                  return { ...tribe, postCount: countSnapshot.data().count };
                } catch (e) {
                  return tribe;
                }
              })
            );

            if (isActive) {
              setForums(forumsWithCounts);
              setTribes(tribesWithCounts);
            }
          } catch (e) {
            console.warn('[ExploreScreen] Failed to fetch accurate post counts:', e);
          }
        }
      } catch (error) {
        console.error("Failed to fetch communities:", error);
        toast.error("Could not load community data");
      } finally {
        if (isActive) {
          setTimeout(() => setIsLoading(false), 500);
        }
      }
    };

    fetchData();

    return () => {
      isActive = false;
    };
  }, [tribeFilter, forumFilter, userProfile.userTags]);

  // Listen for community notifications
  useEffect(() => {
    let unsubscribe: (() => void) | undefined;
    const setupListener = async () => {
      const { getDbSafe } = await import('../services/firebaseConfig');
      const db = await getDbSafe();
      const q = query(
        collection(db, 'notifications'),
        where('audience', '==', 'community'),
        where('expiresAt', '>', new Date()),
        orderBy('expiresAt', 'desc')
      );

      unsubscribe = onSnapshot(q, (snapshot) => {
        const notifs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setCommunityNotifications(notifs);
      });
    };
    setupListener();

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, []);

  const handleOpenIdentityModal = (category: string) => {
    setSelectedCategory(category);
    setShowIdentityModal(true);
  };

  const handleIdentitiesSave = (identities: string[]) => {
    updateProfile({ identities });
    toast.success('Identities updated! Your profile will reflect these changes.');
  };

  const handleTribeClick = (tribe: any) => {
    // Save scroll position before navigating
    if (scrollRef.current) {
      SafeStorage.setItem('scroll_pos_explore', scrollRef.current.scrollTop.toString());
    }
    // Navigate to tribe group screen
    window.dispatchEvent(new CustomEvent('navigateToTribeGroup', { detail: tribe }));
  };

  const handleHistoryClick = (topic: any) => {
    // Save scroll position before navigating
    if (scrollRef.current) {
      SafeStorage.setItem('scroll_pos_explore', scrollRef.current.scrollTop.toString());
    }
    // Navigate to history forum screen
    window.dispatchEvent(new CustomEvent('navigateToHistoryForum', { detail: topic }));
  };

  const handleEventClick = (eventData: any) => {
    // Navigate to event details screen with the specific event data
    window.dispatchEvent(new CustomEvent(EVENTS.NAVIGATE_TO_EVENT_DETAILS, { detail: eventData }));
  };

  // Scroll to top function
  const scrollRef = useRef<HTMLDivElement>(null);

  // Track scroll position for scroll-to-top button
  useEffect(() => {
    const handleScroll = () => {
      if (scrollRef.current) {
        setShowScrollToTop(scrollRef.current.scrollTop > 300);
        // Update search dropdown position if it's showing
        if (showSearchDropdown && searchContainerRef.current) {
          setSearchRect(searchContainerRef.current.getBoundingClientRect());
        }
      }
    };

    const viewport = scrollRef.current;
    if (viewport) {
      viewport.addEventListener('scroll', handleScroll);
      return () => viewport.removeEventListener('scroll', handleScroll);
    }
  }, []);

  const scrollToTop = () => {
    scrollRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Handle Scroll Restoration
  useEffect(() => {
    if (!isLoading && tribes.length > 0 && forums.length > 0 && scrollRef.current) {
      const savedPos = SafeStorage.getItem('scroll_pos_explore');
      if (savedPos) {
        const targetPosition = parseInt(savedPos, 10);
        if (isNaN(targetPosition) || targetPosition <= 0) {
          SafeStorage.removeItem('scroll_pos_explore');
          return;
        }

        console.log(`[ExploreScreen] Restoring scroll to ${targetPosition}`);
        
        // Simple restoration after a short delay to allow layout to settle
        const timer = setTimeout(() => {
          if (scrollRef.current) {
            scrollRef.current.scrollTo(0, targetPosition);
            SafeStorage.removeItem('scroll_pos_explore');
          }
        }, 150);
        return () => clearTimeout(timer);
      }
    }
  }, [isLoading, tribes, forums]);

  return (
    <div className="w-full h-screen relative flex flex-col overflow-hidden bg-light-pattern dark:bg-[#0D0D0D]">
      <div className="absolute inset-0 pointer-events-none">
        <ParallaxBackground speed={0.05} />
      </div>
      <div
        ref={scrollRef}
        className="flex-1 min-h-0 w-full overflow-y-auto"
      >
        <div
          className="w-full max-w-2xl mx-auto px-6 pb-24 relative"
          style={{ paddingTop: 'max(60px, calc(env(safe-area-inset-top) + 24px))' }}
        >
          {/* Header Container with high z-index and relative position to contain the dropdown */}
          <div className="mb-4">
            <div className="flex items-center justify-center mb-4 relative min-h-[40px]">
              {/* Logo and Notifications on the left */}
              <div className="absolute left-0 flex items-center gap-2">
                <img
                  src={String(theme === 'dark' ? logoImageWhite : logoImage)}
                  alt="AfroYap"
                  className="h-7 w-auto"
                />
                <motion.button
                  whileTap={{ scale: 0.95 }}
                  onClick={() => window.dispatchEvent(new CustomEvent('navigateToScreen', { detail: 'notifications' }))}
                  className="relative p-1 rounded-full hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
                >
                  <Bell className="w-6 h-6 text-black dark:text-white" />
                  {unreadCount > 0 && (
                    <span className={`absolute -top-2 -left-2 ${unreadCount > 9 ? 'min-w-[26px] h-[22px] px-1' : 'w-5 h-5'} bg-[#E74C3C] dark:bg-[#3498DB] text-white text-[11px] flex items-center justify-center rounded-full border-2 border-[#fff] dark:border-[#0D0D0D] font-bold`}>
                      {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                  )}
                </motion.button>
              </div>

              {/* Perfectly Centered Title */}
              <h1
                className="text-3xl text-black dark:text-white"
                style={{ fontFamily: "'NarnialOne', 'Narniaone', 'NARNIAONE', 'Poppins', sans-serif" }}
              >
                Explore
              </h1>

              {/* Market / Gold Display on the right */}
              <div className="absolute right-0">
                <div
                  className="flex items-center gap-1.5 bg-black/5 dark:bg-white/5 px-3 py-1.5 rounded-full border border-black/10 dark:border-white/10 backdrop-blur-sm opacity-50 grayscale pointer-events-none"
                >
                  <img src={goldIcon as any} alt="Gold" className="h-5 w-auto object-contain" />
                  <span className="text-black dark:text-white text-sm font-bold">{Number(userProfile.goldBalance || 0).toLocaleString()}</span>
                </div>
              </div>
            </div>

            <div className="relative" ref={searchContainerRef}>
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#666666] dark:text-[#E5E5E5]" />
              <Input
                ref={searchInputRef}
                placeholder="Search countries, forums, tribes..."
                value={searchQuery}
                onFocus={() => { if (searchQuery.trim().length > 0) setShowSearchDropdown(true); }}
                onChange={(e) => setSearchQuery(e.target.value)}
                autoComplete="off"
                className="pl-12 h-12 bg-[#f2deb3] dark:bg-[#1a1a1a] border-none text-[#ea580c] dark:text-white placeholder:text-[#999999] dark:placeholder:text-[#E5E5E5] rounded-xl shadow-[2px_2px_0px_0px_rgba(0,0,0,0.25)] dark:shadow-[2px_2px_0px_0px_rgba(255,255,255,0.1)] relative"
              />
              {isSearching && (
                <div className="absolute right-4 top-1/2 -translate-y-1/2 z-30">
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-orange-500 border-t-transparent"></div>
                </div>
              )}

              {/* Search Dropdown Portal - escapes all clipping containers */}
              {showSearchDropdown && searchQuery.trim().length > 0 && typeof document !== 'undefined' && createPortal(
                <div
                  id="search-dropdown-portal"
                  className="fixed pointer-events-auto"
                  style={{
                    zIndex: 100000,
                    top: searchRect ? searchRect.bottom + 8 : (searchContainerRef.current?.getBoundingClientRect().bottom || 0) + 8,
                    left: searchRect ? searchRect.left : (searchContainerRef.current?.getBoundingClientRect().left || 0),
                    width: searchRect ? searchRect.width : (searchContainerRef.current?.getBoundingClientRect().width || '100%'),
                    maxWidth: 'min(calc(100vw - 48px), 640px)',
                    opacity: 1,
                    visibility: 'visible'
                  }}
                >
                  <div
                    className="w-full rounded-2xl shadow-[0_20px_50px_rgba(0,0,0,0.5)] pointer-events-auto max-h-[60vh] overflow-y-auto scrollbar-hide"
                    style={{
                      backgroundColor: theme === 'dark' ? '#1a1a1a' : '#fdfcf0',
                      border: theme === 'dark' ? '3px solid #3B82F6' : '3px solid #ea580c',
                      opacity: 1,
                      touchAction: 'pan-y',
                      WebkitOverflowScrolling: 'touch',
                      overscrollBehavior: 'contain',
                    }}
                  >
                    <div className="p-2 space-y-4">
                      {isSearching ? (
                        <div className="p-8 text-center space-y-3">
                          <div
                            className="animate-spin rounded-full h-12 w-12 border-t-transparent mx-auto"
                            style={{
                              border: `4px solid ${theme === 'dark' ? '#3B82F6' : '#ea580c'}`,
                              borderTopColor: 'transparent'
                            }}
                          ></div>
                          <p className="text-sm font-bold" style={{ color: theme === 'dark' ? '#3B82F6' : '#ea580c' }}>Searching AfroYap...</p>
                          <p className="text-[10px]" style={{ color: theme === 'dark' ? '#3B82F6' : '#ea580c', opacity: 0.6 }}>Portal Build: 2026-02-10:02:40 | Query: {searchQuery}</p>
                        </div>
                      ) : (
                        <>
                          {searchExploreResults.countries.length === 0 && searchExploreResults.forums.length === 0 && searchExploreResults.tribes.length === 0 && searchExploreResults.regions.length === 0 && businessResults.length === 0 ? (
                            <div className="p-8 text-center space-y-2">
                              <Search className="w-8 h-8 mx-auto" style={{ color: theme === 'dark' ? '#3B82F6' : '#ea580c', opacity: 0.3 }} />
                              <p className="text-sm font-bold" style={{ color: theme === 'dark' ? '#3B82F6' : '#ea580c' }}>No results found</p>
                              <p className="text-xs" style={{ color: theme === 'dark' ? '#999999' : '#666666' }}>Try searching for countries, interests, or tribes.</p>
                            </div>
                          ) : (
                            <>
                              {/* 0. Businesses */}
                              {businessResults.length > 0 && (
                                <div className="p-2">
                                  <h3
                                    className="text-[10px] font-bold mb-1 uppercase tracking-widest px-2"
                                    style={{ color: theme === 'dark' ? '#3B82F699' : '#ea580c99' }}
                                  >Businesses</h3>
                                  <div className="space-y-0.5">
                                    {businessResults.map((business) => (
                                      <div
                                        key={`business-${business.id}`}
                                        onPointerDown={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          setShowSearchDropdown(false);
                                          setSearchQuery('');
                                          window.dispatchEvent(new CustomEvent(EVENTS.NAVIGATE_TO_BUSINESS_PROFILE, { detail: { id: business.id } }));
                                        }}
                                        className="p-3 rounded-lg flex items-center gap-3 cursor-pointer group hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
                                      >
                                        {business.portfolioImages?.[0] && (
                                          <img
                                            src={business.portfolioImages[0]}
                                            alt=""
                                            loading="lazy"
                                            style={{ width: 34, height: 34, borderRadius: 8, objectFit: 'cover', flexShrink: 0 }}
                                          />
                                        )}
                                        <div className="flex-1 min-w-0">
                                          <p
                                            className="text-sm font-bold line-clamp-1"
                                            style={{ color: theme === 'dark' ? '#ffffff' : '#ea580c' }}
                                          >{business.businessName}</p>
                                          {business.description && (
                                            <p className="text-[10px] text-[#666666] dark:text-[#E5E5E5] line-clamp-1">
                                              {business.description}
                                            </p>
                                          )}
                                        </div>
                                        <ChevronRight className="w-4 h-4 text-[#ea580c]/20 group-hover:text-[#ea580c] transition-colors shrink-0" />
                                      </div>
                                    ))}
                                  </div>
                                </div>
                              )}

                              {/* 1. Countries */}
                              {searchExploreResults.countries.length > 0 && (
                                <div className="p-2">
                                  <h3
                                    className="text-[10px] font-bold mb-1 uppercase tracking-widest px-2"
                                    style={{ color: theme === 'dark' ? '#3B82F699' : '#ea580c99' }}
                                  >Countries</h3>
                                  <div className="space-y-0.5">
                                    {searchExploreResults.countries.map((country, idx) => (
                                      <div
                                        key={`country-${idx}`}
                                        onPointerDown={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          setShowSearchDropdown(false);
                                          setSearchQuery('');
                                          window.dispatchEvent(new CustomEvent('navigateToScreen', {
                                            detail: { screen: SCREENS.DISCOVER, country: country.name }
                                          }));
                                        }}
                                        className="p-3 rounded-lg flex items-center justify-between cursor-pointer group hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
                                      >
                                        <div className="flex items-center gap-3 min-w-0">
                                          <FlagImage countryName={country.name} size="w80" className="w-10 h-7 rounded object-contain shadow-sm border border-black/5 shrink-0" />
                                          <CountryName
                                            as="p"
                                            name={country.name}
                                            className="text-sm font-bold min-w-0"
                                            style={{ color: theme === 'dark' ? '#ffffff' : '#ea580c' }}
                                          />
                                        </div>
                                        <div className="flex items-center gap-2">
                                          <span className="text-[10px] font-bold text-[#bc6c25] dark:text-[#a0a0a0]">
                                            {country.members} {Number(country.members) === 1 ? 'yapper' : 'yappers'}
                                          </span>
                                          <ChevronRight className="w-4 h-4 text-[#ea580c]/20 group-hover:text-[#ea580c] transition-colors" />
                                        </div>
                                      </div>
                                    ))}
                                  </div>
                                  {/* See More Link */}
                                  <button
                                    onClick={() => {
                                      setShowSearchDropdown(false);
                                      setSearchQuery('');
                                      window.dispatchEvent(new CustomEvent('navigateToScreen', {
                                        detail: { screen: 'allCountries' }
                                      }));
                                    }}
                                    className="w-full mt-2 p-2 text-center text-xs font-bold rounded-lg hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
                                    style={{ color: theme === 'dark' ? '#3B82F6' : '#ea580c' }}
                                  >
                                    See more countries →
                                  </button>
                                </div>
                              )}

                              {/* 2. Forums */}
                              {searchExploreResults.forums.length > 0 && (
                                <div className="p-2 border-t border-black/5 dark:border-white/5">
                                  <h3
                                    className="text-[10px] font-bold mb-1 uppercase tracking-widest px-2"
                                    style={{ color: theme === 'dark' ? '#3B82F699' : '#ea580c99' }}
                                  >Forums</h3>
                                  <div className="space-y-0.5">
                                    {searchExploreResults.forums.map((forum, idx) => (
                                      <div
                                        key={`forum-${forum.id || idx}`}
                                        onPointerDown={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          setShowSearchDropdown(false);
                                          setSearchQuery('');
                                          handleHistoryClick(forum);
                                        }}
                                        className="p-3 rounded-lg flex items-center justify-between cursor-pointer group hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
                                      >
                                          <div className="flex-1 mr-2 min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap mb-0.5">
                                              <p
                                                className="text-sm font-bold line-clamp-1"
                                                style={{ color: theme === 'dark' ? '#ffffff' : '#ea580c' }}
                                              >{forum.title || forum.name}</p>
                                              {(forum as any).mentions > 0 && (
                                                <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-black/10 dark:bg-white/10 text-[#666] dark:text-[#a0a0a0] whitespace-nowrap">
                                                  {(forum as any).mentions} {(forum as any).mentions === 1 ? 'mention' : 'mentions'}
                                                </span>
                                              )}
                                            </div>
                                            <p className="text-[10px] text-[#666666] dark:text-[#E5E5E5] line-clamp-1">{forum.description}</p>
                                          </div>
                                          <ChevronRight className="w-4 h-4 text-[#ea580c]/20 group-hover:text-[#ea580c] transition-colors shrink-0" />
                                      </div>
                                    ))}
                                  </div>
                                  {/* See More Link */}
                                  <button
                                    onPointerDown={(e) => {
                                      e.preventDefault();
                                      setShowSearchDropdown(false);
                                      setSearchQuery('');
                                      window.dispatchEvent(new CustomEvent('navigateToScreen', {
                                        detail: { screen: 'allForums' }
                                      }));
                                    }}
                                    className="w-full mt-2 p-2 text-center text-xs font-bold rounded-lg hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
                                    style={{ color: theme === 'dark' ? '#3B82F6' : '#ea580c' }}
                                  >
                                    See more forums →
                                  </button>
                                </div>
                              )}

                              {/* 3. Tribes */}
                              {searchExploreResults.tribes.length > 0 && (
                                <div className="p-2 border-t border-black/5 dark:border-white/5">
                                  <h3
                                    className="text-[10px] font-bold mb-1 uppercase tracking-widest px-2"
                                    style={{ color: theme === 'dark' ? '#3B82F699' : '#ea580c99' }}
                                  >Tribes</h3>
                                  <div className="space-y-0.5">
                                    {searchExploreResults.tribes.map((tribe, idx) => (
                                      <div
                                        key={`tribe-${tribe.id || idx}`}
                                        onPointerDown={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          setShowSearchDropdown(false);
                                          setSearchQuery('');
                                          handleTribeClick(tribe);
                                        }}
                                        className="p-3 rounded-lg flex items-center justify-between cursor-pointer group hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
                                      >
                                          <div className="flex-1 mr-2 min-w-0">
                                            <div className="flex items-center gap-2 flex-wrap mb-0.5">
                                              <p
                                                className="text-sm font-bold line-clamp-1"
                                                style={{ color: theme === 'dark' ? '#ffffff' : '#ea580c' }}
                                              >{tribe.name || tribe.title}</p>
                                              {(tribe as any).mentions > 0 && (
                                                <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-black/10 dark:bg-white/10 text-[#666] dark:text-[#a0a0a0] whitespace-nowrap">
                                                  {(tribe as any).mentions} {(tribe as any).mentions === 1 ? 'mention' : 'mentions'}
                                                </span>
                                              )}
                                            </div>
                                            <p className="text-[10px] text-[#666666] dark:text-[#E5E5E5] line-clamp-1">{tribe.description}</p>
                                          </div>
                                          <ChevronRight className="w-4 h-4 text-[#ea580c]/20 group-hover:text-[#ea580c] transition-colors shrink-0" />
                                      </div>
                                    ))}
                                  </div>
                                  {/* See More Link */}
                                  <button
                                    onPointerDown={(e) => {
                                      e.preventDefault();
                                      setShowSearchDropdown(false);
                                      setSearchQuery('');
                                      window.dispatchEvent(new CustomEvent('navigateToScreen', {
                                        detail: { screen: 'allTribes' }
                                      }));
                                    }}
                                    className="w-full mt-2 p-2 text-center text-xs font-bold rounded-lg hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
                                    style={{ color: theme === 'dark' ? '#3B82F6' : '#ea580c' }}
                                  >
                                    See more tribes →
                                  </button>
                                </div>
                              )}

                              {/* 4. Ethnic/Regional Flags */}
                              {searchExploreResults.regions.length > 0 && (
                                <div className="p-2 border-t border-black/5 dark:border-white/5">
                                  <h3
                                    className="text-[10px] font-bold mb-1 uppercase tracking-widest px-2"
                                    style={{ color: theme === 'dark' ? '#3B82F699' : '#ea580c99' }}
                                  >Ethnic Groups</h3>
                                  <div className="space-y-0.5">
                                    {searchExploreResults.regions.map((region, idx) => (
                                      <div
                                        key={`region-${region.code || idx}`}
                                        onPointerDown={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          setShowSearchDropdown(false);
                                          setSearchQuery('');
                                          // Navigate to AllIdentitiesScreen or specific ethnic group page
                                          window.dispatchEvent(new CustomEvent('navigateToScreen', {
                                            detail: { screen: SCREENS.ALL_COUNTRIES, filter: region.name }
                                          }));
                                        }}
                                        className="p-3 rounded-lg flex items-center justify-between cursor-pointer group hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
                                      >
                                        <div>
                                          <p
                                            className="text-sm font-bold line-clamp-1"
                                            style={{ color: theme === 'dark' ? '#ffffff' : '#ea580c' }}
                                          >{region.name}</p>
                                          <p className="text-[10px] text-[#666666] dark:text-[#E5E5E5] line-clamp-1">{region.region}</p>
                                        </div>
                                        <ChevronRight className="w-4 h-4 text-[#ea580c]/20 group-hover:text-[#ea580c] transition-colors" />
                                      </div>
                                    ))}
                                  </div>
                                  {/* See More Link */}
                                  <button
                                    onPointerDown={(e) => {
                                      e.preventDefault();
                                      setShowSearchDropdown(false);
                                      setSearchQuery('');
                                      window.dispatchEvent(new CustomEvent('navigateToScreen', {
                                        detail: { screen: SCREENS.ALL_COUNTRIES }
                                      }));
                                    }}
                                    className="w-full mt-2 p-2 text-center text-xs font-bold rounded-lg hover:bg-black/5 dark:hover:bg-white/5 transition-colors"
                                    style={{ color: theme === 'dark' ? '#3B82F6' : '#ea580c' }}
                                  >
                                    See more ethnic groups →
                                  </button>
                                </div>
                              )}
                            </>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                </div>,
                document.body
              )}
            </div>
          </div>

          <>
            {/* Community Notifications */}
            <div className="mb-6 space-y-2">
              <AnimatePresence>
                {communityNotifications.map((notif) => (
                  <motion.div
                    key={notif.id}
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="p-3 rounded-xl bg-linear-to-r from-blue-500/10 to-purple-500/10 border border-blue-500/20 flex items-start gap-3"
                  >
                    <div className="p-2 rounded-full bg-blue-500/20 text-blue-600 dark:text-blue-400">
                      <Users className="w-4 h-4" />
                    </div>
                    <div>
                      <h4 className="text-sm font-bold text-[#ea580c] dark:text-[#E5E5E5]">Community Update</h4>
                      <p className="text-xs text-[#666666] dark:text-[#E5E5E5]">{notif.message}</p>
                    </div>
                  </motion.div>
                ))}
              </AnimatePresence>
            </div>



            {/* Businesses — a shuffled deck of active business profiles.
                Login-gated with the rest of this screen (ProtectedRoute); the
                only logged-out surface for a business is its direct link. */}
            <BusinessExploreDeck />

            {/* Events Section */}
            <section className="mb-8 relative z-0">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <Calendar className="w-6 h-6 text-[#F39C12] dark:text-[#4478ea]" />
                  <h2 className="text-base font-bold text-[#ea580c] dark:text-white">Events</h2>
                </div>
                {/* Legion Remote — visible only to legion rank */}
                {userProfile.rank === 'legion' && (
                  <motion.button
                    whileTap={{ scale: 0.95 }}
                    onClick={() => setShowLegionEventManager(true)}
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-bold text-white"
                    style={{ background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)' }}
                  >
                    <ShieldAlert className="w-3.5 h-3.5" />
                    Manage Events
                  </motion.button>
                )}
              </div>

              {/* Live Event Banner — Firestore-driven, falls back to static image */}
              {(() => {
                const displayEvents = (liveEvents && liveEvents.length > 0) ? liveEvents : [{ id: 'static', title: 'Food of the Week: Pilau', imageUrl: String(foodOfDayImage), foodSections: PILAU_SECTIONS }];
                const current = displayEvents[activeEventIndex % displayEvents.length];
                
                const variants = {
                    enter: (dir: number) => ({ x: dir > 0 ? '100%' : '-100%', opacity: 0 }),
                    center: { x: 0, opacity: 1 },
                    exit: (dir: number) => ({ x: dir > 0 ? '-100%' : '100%', opacity: 0 })
                };

                return (
                  <div 
                    className="relative w-full rounded-3xl overflow-hidden shadow-2xl bg-white/5 border border-white/10" 
                    style={{ aspectRatio: '16/10' }}
                  >
                    <AnimatePresence initial={false} custom={direction}>
                        <motion.div
                            key={current.id}
                            custom={direction}
                            variants={variants}
                            initial="enter"
                            animate="center"
                            exit="exit"
                            transition={{ x: { type: "tween", duration: 0.5, ease: "easeInOut" }, opacity: { duration: 0.3 } }}
                            onClick={() => handleEventClick(current)}
                            className="absolute inset-0 cursor-pointer group"
                        >
                            <img
                                src={current.imageUrl || String(foodOfDayImage)}
                                alt={current.title || 'Event'}
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700 pointer-events-none"
                            />
                            {/* Overlay with event title from Firestore */}
                            {current.title && (
                                <div 
                                    className="absolute bottom-0 left-0 w-full pointer-events-none flex flex-col justify-end p-6 pb-9 pt-24"
                                    style={{ background: 'linear-gradient(to top, rgba(0,0,0,1) 0%, rgba(0,0,0,0.8) 40%, rgba(0,0,0,0.4) 70%, transparent 100%)' }}
                                >
                                    <p 
                                        className="text-white font-bold text-xl mb-1.5" 
                                        style={{ textShadow: '0 2px 12px rgba(0,0,0,1)', fontFamily: "'NarnialOne', sans-serif" }}
                                    >
                                        {current.title}
                                    </p>
                                    {current.description && (
                                        <p className="text-white text-[13px] line-clamp-1 max-w-[95%] opacity-90 font-medium leading-tight" style={{ textShadow: '0 1px 6px rgba(0,0,0,0.8)' }}>
                                            {current.description}
                                        </p>
                                    )}
                                </div>
                            )}
                        </motion.div>
                    </AnimatePresence>

                    {/* Prev / Next (only shown if multiple events) */}
                    {displayEvents.length > 1 && (
                      <>
                        <motion.button
                          onClick={(e) => { 
                              e.stopPropagation(); 
                              setDirection(-1);
                              setActiveEventIndex(i => (i - 1 + displayEvents.length) % displayEvents.length); 
                          }}
                          whileTap={{ scale: 0.9 }}
                          className="absolute left-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/40 hover:bg-black/60 backdrop-blur-md flex items-center justify-center text-white z-20 border border-white/10 transition-colors"
                        >
                          <ChevronLeft className="w-5 h-5" />
                        </motion.button>
                        <motion.button
                          onClick={(e) => { 
                              e.stopPropagation(); 
                              setDirection(1);
                              setActiveEventIndex(i => (i + 1) % displayEvents.length); 
                          }}
                          whileTap={{ scale: 0.9 }}
                          className="absolute right-3 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/40 hover:bg-black/60 backdrop-blur-md flex items-center justify-center text-white z-20 border border-white/10 transition-colors"
                        >
                          <ChevronRight className="w-5 h-5" />
                        </motion.button>
                        {/* Dot indicators */}
                        <div className="absolute bottom-3 left-0 right-0 flex justify-center gap-1.5 pointer-events-none z-20">
                          {displayEvents.map((_, i) => (
                            <div key={i} className={`w-1.5 h-1.5 rounded-full transition-all duration-300 ${i === activeEventIndex % displayEvents.length ? 'bg-orange-500 w-4' : 'bg-white/30'}`} />
                          ))}
                        </div>
                      </>
                    )}
                  </div>
                );
              })()}
            </section>

            {/* Forums Section */}
            <section className="mb-8">
              <div className="flex items-center justify-between mb-4 gap-4">
                <div className="flex items-center gap-2">
                  <BookOpen className="w-6 h-6 text-[#F39C12] dark:text-[#4478ea]" />
                  <h2 className="text-base font-bold text-[#ea580c] dark:text-white">
                    Forums
                  </h2>
                  <Button
                    onClick={() => window.dispatchEvent(new CustomEvent('openCreateForumModal'))}
                    className="h-8 text-xs text-white shadow-md border-none ml-1"
                    style={{
                      backgroundImage: theme === 'dark'
                        ? 'linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)'
                        : 'linear-gradient(135deg, #F39C12 0%, #E74C3C 100%)'
                    }}
                  >
                    <BookOpen className="w-3 h-3 mr-1" />
                    Create Forum
                  </Button>
                </div>
                <Button
                  variant="ghost"
                  onClick={() => window.dispatchEvent(new CustomEvent('navigateToScreen', { detail: 'allForums' }))}
                  className="px-3 py-1 rounded-full text-white shadow-md text-xs border-none shrink-0"
                  style={{
                    backgroundImage: theme === 'dark'
                      ? 'linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)'
                      : 'linear-gradient(135deg, #F39C12 0%, #E74C3C 100%)',
                    textShadow: '0px 1px 3px rgba(0,0,0,0.8)'
                  }}
                >
                  Show More →
                </Button>
              </div>

              <CommunityFilterBar
                selectedFilter={forumFilter}
                onFilterChange={setForumFilter}
                className="mb-2"
              />


              {isLoading ? (
                <div className="grid grid-cols-2 gap-4">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <div key={i} className="h-32 rounded-2xl animate-pulse bg-black/10 dark:bg-white/10" />
                  ))}
                </div>
              ) : forums.length === 0 ? (
                <div className="text-center py-12 text-gray-500 text-sm flex flex-col items-center justify-center">
                  <p>No forums found.</p>
                  <p className="text-xs mt-1">Start a discussion!</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-4" data-tour="guide-forums">
                  {forums.map((forum, index) => {
                    // Debug logging
                    if (index === 0) console.log('[ExploreScreen] Forum data:', { id: forum.id, bannerUrl: forum.bannerUrl, image: forum.image, bannerImage: forum.bannerImage });
                    
                    return (
                    <PressableCard
                      key={forum.id || index}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: index * 0.05 }}
                      onClick={() => handleHistoryClick(forum)}
                      className="relative h-32 rounded-2xl overflow-hidden cursor-pointer shadow-lg group"
                      style={{ backgroundColor: '#2a2a2a' }}
                    >
                      {/* Background Image or Gradient */}
                      <div 
                        className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-110"
                        style={{ 
                          background: (forum.bannerUrl || forum.image || forum.bannerImage) 
                            ? `url('${forum.bannerUrl || forum.image || forum.bannerImage}') center/cover no-repeat #2a2a2a` 
                            : getGradient(forum.id || forum.title || forum.name)
                        }}
                      />
                      
                      {/* Gradient Overlay */}
                      <div 
                        className="absolute inset-0 backdrop-blur-[1px] transition-all duration-300" 
                        style={{ background: 'radial-gradient(ellipse at center, rgba(0,0,0,0.1) 20%, rgba(0,0,0,0.85) 100%)' }}
                      />
                      
                      {/* Content */}
                      <div className="absolute inset-x-0 bottom-0 p-3">
                        <h3 
                          className="text-sm font-bold text-white line-clamp-2 leading-tight mb-1 pl-1"
                          style={{ textShadow: '0 2px 4px rgba(0,0,0,0.8)' }}
                        >
                          {forum.title || forum.name}
                        </h3>
                        <div 
                          className="flex items-center gap-2 text-[10px] text-white/80 font-medium pl-1"
                          style={{ textShadow: '0 2px 4px rgba(0,0,0,0.8)' }}
                        >
                          <span className="flex items-center gap-1">
                            <Users className="w-3 h-3" style={{ filter: 'drop-shadow(0 2px 2px rgba(0,0,0,0.5))' }} /> 
                            {forum.memberCount ? (
                              forum.memberCount >= 1000 
                                ? `${(forum.memberCount / 1000).toFixed(1)}k` 
                                : forum.memberCount
                            ) : '1'}
                          </span>
                          <span className="w-1 h-1 rounded-full bg-white/50" />
                          <span>{forum.postCount || 0} posts</span>
                        </div>
                      </div>

                      {/* Top Right Icon */}
                      <div className="absolute top-2 right-2 p-1.5 rounded-full bg-black/20 backdrop-blur-sm">
                        <BookOpen className="w-3 h-3 text-white/90" style={{ filter: 'drop-shadow(0 2px 2px rgba(0,0,0,0.5))' }} />
                      </div>
                    </PressableCard>
                    );
                  })}
                </div>
              )}
            </section>

            {/* Tribes Section */}
            <section className="mb-8">
              <div className="flex items-center justify-between mb-4 gap-4">
                <div className="flex items-center gap-2">
                  <Users className="w-6 h-6 text-[#F39C12] dark:text-[#4478ea]" />
                  <h2 className="text-base font-bold text-[#ea580c] dark:text-white">
                    Tribes (Groups)
                  </h2>
                  <Button
                    onClick={() => window.dispatchEvent(new CustomEvent('openCreateTribeModal'))}
                    className="h-8 text-xs text-white shadow-md border-none ml-1"
                    style={{
                      backgroundImage: theme === 'dark'
                        ? 'linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)'
                        : 'linear-gradient(135deg, #F39C12 0%, #E74C3C 100%)'
                    }}
                  >
                    <Users className="w-3 h-3 mr-1" />
                    Establish Tribe
                  </Button>
                </div>
                <Button
                  variant="ghost"
                  onClick={() => window.dispatchEvent(new CustomEvent('navigateToScreen', { detail: 'allTribes' }))}
                  className="px-3 py-1 rounded-full text-white shadow-md text-xs border-none shrink-0"
                  style={{
                    backgroundImage: theme === 'dark'
                      ? 'linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)'
                      : 'linear-gradient(135deg, #F39C12 0%, #E74C3C 100%)',
                    textShadow: '0px 1px 3px rgba(0,0,0,0.8)'
                  }}
                >
                  Show More →
                </Button>
              </div>

              <CommunityFilterBar
                selectedFilter={tribeFilter}
                onFilterChange={setTribeFilter}
                className="mb-2"
                showUniversityFilter={true}
              />


              {isLoading ? (
                <div className="grid grid-cols-2 gap-4">
                  {Array.from({ length: 4 }).map((_, i) => (
                    <div key={i} className="h-32 rounded-2xl animate-pulse bg-black/10 dark:bg-white/10" />
                  ))}
                </div>
              ) : tribes.length === 0 ? (
                <div className="text-center py-12 text-gray-500 text-sm flex flex-col items-center justify-center">
                  <p>No tribes found.</p>
                  <p className="text-xs mt-1">Be the first to establish one!</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-4" data-tour="guide-tribes">
                  {tribes.map((tribe, index) => {
                    // Debug logging  
                    if (index === 0) console.log('[ExploreScreen] Tribe data:', { id: tribe.id, bannerUrl: tribe.bannerUrl, image: tribe.image });
                    
                    return (
                    <PressableCard
                      key={tribe.id || index}
                      initial={{ opacity: 0, scale: 0.9 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: index * 0.05 }}
                      onClick={() => handleTribeClick(tribe)}
                      className="relative h-32 rounded-2xl overflow-hidden cursor-pointer shadow-lg group"
                      style={{ backgroundColor: '#2a2a2a' }}
                    >
                      {/* Background Image or Gradient */}
                      <div 
                        className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-110"
                        style={{ 
                          background: (tribe.bannerUrl || tribe.image) 
                            ? `url('${tribe.bannerUrl || tribe.image}') center/cover no-repeat #2a2a2a` 
                            : getGradient(tribe.id || tribe.name)
                        }}
                      />
                      
                      {/* Gradient Overlay */}
                      <div
                        className="absolute inset-0 backdrop-blur-[1px] transition-all duration-300 z-[1]"
                        style={{ background: 'radial-gradient(ellipse at center, rgba(0,0,0,0.1) 20%, rgba(0,0,0,0.85) 100%)' }}
                      />

                      {/* University logo — centered, no masking, renders behind text */}
                      {(tribe as any).tribeType === 'university' && (tribe as any).schoolLogoUrl && (
                        <img
                          src={(tribe as any).schoolLogoUrl}
                          alt=""
                          className="absolute z-[2] pointer-events-none"
                          style={{
                            top: '50%',
                            left: '50%',
                            transform: 'translate(-50%, -50%)',
                            maxWidth: 72,
                            maxHeight: 72,
                            width: 'auto',
                            height: 'auto',
                            objectFit: 'contain',
                            filter: 'drop-shadow(0 2px 8px rgba(0,0,0,0.9))',
                          }}
                        />
                      )}

                      {/* Bottom gradient — reinforces text legibility when logo is light/busy */}
                      <div className="absolute inset-x-0 bottom-0 h-14 z-[3]" style={{ background: 'linear-gradient(to top, rgba(0,0,0,0.85) 0%, transparent 100%)' }} />

                      {/* Content */}
                      <div className="absolute inset-x-0 bottom-0 p-3 z-[4]">
                        <h3
                          className="text-sm font-bold text-white line-clamp-2 leading-tight mb-1 pl-1"
                          style={{ textShadow: '0 2px 4px rgba(0,0,0,0.8)' }}
                        >
                          {tribe.name}
                        </h3>
                        <div
                          className="flex items-center gap-2 text-[10px] text-white/80 font-medium pl-1"
                          style={{ textShadow: '0 2px 4px rgba(0,0,0,0.8)' }}
                        >
                          <span className="flex items-center gap-1">
                            <Users className="w-3 h-3" style={{ filter: 'drop-shadow(0 2px 2px rgba(0,0,0,0.5))' }} />
                            {tribe.memberCount ? (
                              tribe.memberCount >= 1000
                                ? `${(tribe.memberCount / 1000).toFixed(1)}k`
                                : tribe.memberCount
                            ) : '1'}
                          </span>
                          <span className="w-1 h-1 rounded-full bg-white/50" />
                          <span>{tribe.postCount || 0} posts</span>
                        </div>
                      </div>

                      <div className="absolute top-2 right-2 p-1.5 rounded-full bg-black/20 backdrop-blur-sm z-[4]">
                        <Users className="w-3 h-3 text-white/90" style={{ filter: 'drop-shadow(0 2px 2px rgba(0,0,0,0.5))' }} />
                      </div>

                      {/* University badge — always shown when tribeType is 'university' */}
                      {(tribe as any).tribeType === 'university' && (
                        <div className="absolute top-2 left-2 flex items-center gap-1 px-1.5 py-1 rounded-full bg-[#FFD700]/25 backdrop-blur-sm border border-[#FFD700]/40 z-[4]">
                          <GraduationCap className="w-3 h-3 text-[#FFD700]" style={{ filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.6))' }} />
                        </div>
                      )}
                    </PressableCard>
                    );
                  })}
                </div>
              )}
            </section>



            {/* Countries Section */}
            <section className="mb-12">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <MapPin className="w-6 h-6 text-[#F39C12] dark:text-[#4478ea]" />
                  <h2 className="text-base font-bold text-[#ea580c] dark:text-white">
                    Top Yappers
                  </h2>
                </div>
                <Button
                  variant="ghost"
                  onClick={() => window.dispatchEvent(new CustomEvent('navigateToScreen', { detail: 'allCountries' }))}
                  className="px-3 py-1 rounded-full text-white shadow-md text-xs border-none"
                  style={{
                    backgroundImage: theme === 'dark'
                      ? 'linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)'
                      : 'linear-gradient(135deg, #F39C12 0%, #E74C3C 100%)',
                    textShadow: '0px 1px 3px rgba(0,0,0,0.8)'
                  }}
                >
                  Show More →
                </Button>
              </div>
              {/* items-start: grid rows stretch their items to the tallest by
                  default, so one two-line card used to make its neighbour two
                  lines tall as well. Each card now sizes to its own content. */}
              <div className="grid grid-cols-2 gap-4 items-start">
                {countries.map((country, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: index * 0.05 }}
                    onClick={() => window.dispatchEvent(new CustomEvent('navigateToScreen', {
                      detail: { screen: SCREENS.DISCOVER, country: country.name }
                    }))}
                    className="p-4 rounded-2xl bg-[#f2deb3] dark:bg-[#1a1a1a] border border-black/5 dark:border-white/10 hover:border-[#F39C12] transition-colors cursor-pointer subtle-shadow"
                  >
                    {/* minHeight, not a fixed height: a one-line card keeps the
                        size it has always had, and only a name that wraps makes
                        its own card taller. 40px is the one-line text block
                        (name + count); the card's p-4 adds the rest.

                        items-center keeps the flag and the text block centred
                        together whatever the card's height. */}
                    <div className="flex items-center gap-3" style={{ minHeight: 40 }}>
                      {/* shrink-0: without it the flag is the first thing the
                          browser squashes when the name is long. */}
                      <FlagImage countryName={country.name} size="w80" className="w-8 h-6 rounded object-contain shrink-0" />
                      {/* min-w-0 is what actually lets the name wrap. A flex
                          child's default min-width is auto, meaning "never
                          narrower than my content", so an unbreakable name
                          pushed the box wider than the card instead of
                          wrapping inside it. */}
                      <div className="min-w-0">
                        <CountryName
                          as="h3"
                          name={country.name}
                          className="text-[#ea580c] dark:text-white"
                        />
                        <p className="text-sm text-[#666666] dark:text-[#E5E5E5]">{country.members} {Number(country.members) === 1 ? 'yapper' : 'yappers'}</p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </section>

            <section id="identity-section" className="mb-12">
              <div className="flex items-center gap-2 mb-4 mt-8">
                <Tag className="w-6 h-6 text-[#F39C12] dark:text-[#4478ea]" />
                <h2 className="text-base font-bold text-[#ea580c] dark:text-white">
                  Identity
                </h2>
              </div>
              <div className="space-y-4">
                {identityCategories.map((category, index) => {
                  const categoryIdentities = identityData[category] || [];
                  let userSelected = userProfile.identities?.filter((id: string) => {
                    if (categoryIdentities.includes(id)) return true;
                    // Fuzzy match for slash-separated identities that might have been reordered
                    return categoryIdentities.some(cid => 
                      cid.toLowerCase().split('/').sort().join('/') === id.toLowerCase().split('/').sort().join('/') ||
                      cid.toLowerCase().split('/').includes(id.toLowerCase())
                    );
                  }).map(id => {
                    // Map it to the exact catalog string if a fuzzy match is found
                    const exactMatch = categoryIdentities.find(cid => 
                      cid.toLowerCase() === id.toLowerCase() ||
                      cid.toLowerCase().split('/').sort().join('/') === id.toLowerCase().split('/').sort().join('/') ||
                      cid.toLowerCase().split('/').includes(id.toLowerCase())
                    );
                    return exactMatch || id;
                  }) || [];
                  
                  // Support legacy ethnicGroup if present and not in identities
                  if (category === 'Ethnic Groups' && userProfile.ethnicGroup) {
                    const match = categoryIdentities.find(id => {
                      const idLower = id.toLowerCase();
                      const userLower = userProfile.ethnicGroup?.toLowerCase() || '';
                      if (idLower === userLower) return true;
                      if (idLower.split('/').sort().join('/') === userLower.split('/').sort().join('/')) return true;
                      if (idLower.split('/').includes(userLower) || userLower.split('/').includes(idLower)) return true;
                      return false;
                    });
                    if (match && !userSelected.includes(match)) {
                      userSelected = [match, ...userSelected];
                    }
                  }
                  
                  const samples = categoryIdentities
                    .filter((id: string) => !userSelected.includes(id))
                    .slice(0, 5);
                  const style = categoryStyles[category] || categoryStyles['Interests'];

                  return (
                    <div key={index}>
                      <div className="flex items-center justify-between mb-2">
                        <h3 className="text-lg font-bold text-black dark:text-white">
                          {category}
                        </h3>
                        <Button
                          onClick={() => {
                            setSelectedCategory(category);
                            setShowIdentityModal(true);
                          }}
                          variant="ghost"
                          className="px-3 py-1 rounded-full text-white shadow-md text-xs border-none"
                          style={{
                            backgroundImage: theme === 'dark'
                              ? 'linear-gradient(135deg, #3B82F6 0%, #1D4ED8 100%)'
                              : 'linear-gradient(135deg, #F39C12 0%, #E74C3C 100%)',
                            textShadow: '0px 1px 3px rgba(0,0,0,0.8)'
                          }}
                        >
                          Show More →
                        </Button>
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {/* Selected Identities */}
                        {userSelected.map((item: string, i: number) => (
                          <IdentityTag key={`selected-${i}`} tag={item} variant="profile" />
                        ))}
                        {/* Sample Identities */}
                        {samples.map((item: string, i: number) => (
                          <IdentityTag key={`sample-${i}`} tag={item} variant="standard" />
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </section>
          </>
        </div>
      </div>



      {/* Scroll to Top Button */}
      {
        showScrollToTop && (
          <motion.div
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            className="fixed bottom-4 right-4 z-50"
          >
            <motion.button
              whileTap={{ scale: 0.95 }}
              onClick={scrollToTop}
              className="w-12 h-12 rounded-full bg-linear-to-r from-[#F39C12] dark:from-[#4478ea] to-[#E74C3C] dark:to-[#2d1ef1] flex items-center justify-center shadow-lg button-shadow"
            >
              <ArrowUp className="w-6 h-6 text-white" />
            </motion.button>
          </motion.div>
        )
      }

      <IdentityExpansionModal
        isOpen={showIdentityModal}
        onClose={() => setShowIdentityModal(false)}
        category={selectedCategory}
        onSave={handleIdentitiesSave}
        initialSelected={userProfile.identities}
      />

      {/* Legion Remote — Event Manager (legion rank only) */}
      <LegionEventManagerModal
        isOpen={showLegionEventManager}
        onClose={() => setShowLegionEventManager(false)}
        moderatorId={String(userProfile.id || '')}
      />
    </div>
  );
}
